/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import { useState } from 'react';
import * as React from 'react';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';

import { ChevronDown } from '../../icons/Icons';
import { ANIMATIONS, INTERNAL_LINKS } from '../../utils';
import { Heading, Text } from '../design-system';

import { LinkWithQuery } from './LinkWithQuery';

const Wrapper = styled(motion.div)<{ $noBkg: boolean }>`
  padding: 80px 0;
  background: ${({ $noBkg, theme }) =>
    $noBkg ? 'transparent' : theme.backgroundColor};
  width: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding-top: 60px;
  }
`;

const DescText = styled(Text)`
  margin-top: 35px;
  text-align: center;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 604px;
  width: 100%;
  padding: 0 30px;
  margin: 0 auto;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:hover {
    background: ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.1)};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    padding: 20px 0;
  }
`;

const ItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Question = styled.div`
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-size: 18px;
  line-height: 24px;
  letter-spacing: -0.5px;
`;

const Answer = styled(Text).attrs({ size: 'medium' })<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 12px;

  ul {
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  strong {
    font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
  }
`;

export interface FAQItemType {
  answer: ReactNode;
  question: string;
}

interface FAQProps {
  items: FAQItemType[];
  noBkg?: boolean;
}

export const FAQ: React.ComponentType<FAQProps> = React.memo(
  ({ items, noBkg = false }) => {
    return (
      <Wrapper {...ANIMATIONS.SimpleFadeIn} $noBkg={noBkg}>
        <Inner>
          <Top>
            <Heading
              css={`
                margin: 15px 0px;
              `}
              renderAs="h2">
              FAQ
            </Heading>
          </Top>
          <List>
            {items.map((item, index) => (
              <FAQItem
                key={index}
                answer={item.answer}
                question={item.question}
              />
            ))}
          </List>
          <DescText size="medium">
            Can't find your answer here?{' '}
            <LinkWithQuery to={INTERNAL_LINKS.ContactUs}>
              Get in touch
            </LinkWithQuery>
            .
          </DescText>
        </Inner>
      </Wrapper>
    );
  }
);

const ChevronIcon = styled(ChevronDown)<{ $isOpen: boolean }>`
  transform: rotate(${({ $isOpen }) => ($isOpen ? '0deg' : '-90deg')});
`;

const FAQItem: React.ComponentType<FAQItemType> = React.memo(
  ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <Item onClick={() => setIsOpen(!isOpen)}>
        <ItemTop>
          <Question>{question}</Question>
          <ChevronIcon $isOpen={isOpen} />
        </ItemTop>
        <Answer $isOpen={isOpen}>{answer}</Answer>
      </Item>
    );
  }
);
